import {
  Box,
  Button,
  FormHelperText,
  LinearProgress,
  Link,
  Paper,
} from '@mui/material';
import {
  useCallback,
  useEffect,
} from 'react';
import Upload from '@components/molecules/form/Upload';
import { FilePreview } from '@components/atoms/FilePreview';
import { uploadField } from '@lib/form';
import { uploadFile } from '@lib/upload';
import { useTranslation } from 'next-i18next';
import PropTypes from 'prop-types';
import { StandaloneFieldLabel } from '../FieldLabel';
import getFieldStatus from '../fieldStatus';

export default function UploadField(props) {
  const {
    name,
    disabled,
    value,
    error,
    touched,
    label = '',
    isRequired,
    helperText = '',
    handleChange,
    maxFiles,
    readOnly,
    setFieldTouched,
  } = props;

  const { t } = useTranslation();

  const handleUploadProgress = useCallback((event) => {
    // console.log(event);
  }, []);

  useEffect(() => {
    const fileToUpload = value.find((file) => file.isPending && !file.isUploading);

    if (!fileToUpload) {
      return;
    }

    handleChange(sortFiles([
      ...value.filter((file) => file.id !== fileToUpload.id),
      {
        ...fileToUpload,
        isPending: false,
        isUploading: true,
      },
    ]));

    uploadFile(fileToUpload.file, handleUploadProgress)
      .then((uploadedFile) => {
        handleChange(
          sortFiles([
            ...value.filter((file) => file.id !== fileToUpload.id),
            {
              ...fileToUpload,
              file: null,
              id: uploadedFile.id,
              isPending: false,
              isUploading: false,
              url: uploadedFile.url,
              width: uploadedFile.width,
              height: uploadedFile.height,
              formats: uploadedFile.formats,
            },
          ]),
        );
      })
      .catch((e) => {
        handleChange(sortFiles(
          value.filter((file) => file.id !== fileToUpload.id),
        ));
      });
  }, [
    value,
    handleChange,
    handleUploadProgress,
  ]);

  const handleDrop = async (selectedFiles) => {
    handleChange(sortFiles([
      ...value,
      ...selectedFiles.map((file) => ({
        id: `pending_${new Date().getTime()}`,
        name: file.name,
        isPending: true,
        isUploading: false,
        file,
      })),
    ]));
    setFieldTouched(name);
  };

  const handleDeleteFile = (id) => async () => {
    try {
      handleChange(sortFiles(
        value.filter((file) => file.id !== id),
      ));
    } catch (e) {
      console.error(e.message);
    }
  };

  const handleOpenInNewTab = ({ url }) => {
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const isLocked = disabled || readOnly;
  const { hasError, message } = getFieldStatus({ error, touched, helperText });

  return (
    <Box flex={1}>
      <StandaloneFieldLabel isRequired={isRequired} label={label} />

      {!isLocked && (
        <Upload
          accept={ACCEPTED_FILE_TYPES}
          disabled={disabled || (maxFiles && value.length >= maxFiles)}
          hint={t('components.molecules.form.Upload.hint', { count: maxFiles ?? 0 })}
          maxFiles={maxFiles}
          onDrop={handleDrop}
        />
      )}

      {value.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            mt: isLocked ? 0 : 1.25,
          }}
        >
          {value.map((item) => (
            <Paper
              key={item.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                p: 1.25,
              }}
              variant="outlined"
            >
              <FilePreview
                onClick={() => handleOpenInNewTab(item)}
                size={36}
                sx={{ width: 36, flex: 'none' }}
                url={
                  item.formats?.small?.url
                  ?? item.formats?.medium?.url
                  ?? item.formats?.large?.url
                  ?? item.url
                }
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Link
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handleOpenInNewTab(item);
                  }}
                  sx={{
                    display: 'block',
                    fontWeight: 600,
                    color: 'text.primary',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  underline="hover"
                  variant="body2"
                >
                  {item.name}
                </Link>
                {item.isUploading && (
                  // A determinate bar would be a lie — `handleUploadProgress` receives the
                  // events but the field never stores a percentage.
                  <LinearProgress sx={{ mt: 0.75 }} />
                )}
              </Box>
              {!isLocked && (
                <Button
                  color="error"
                  disabled={item.isUploading}
                  onClick={handleDeleteFile(item.id)}
                  size="small"
                  sx={{ flex: 'none', fontWeight: 600 }}
                  variant="text"
                >
                  {t('components.molecules.form.fields.UploadField.deleteFileTooltip')}
                </Button>
              )}
            </Paper>
          ))}
        </Box>
      )}

      {message && (
        <FormHelperText error={hasError}>
          {message}
        </FormHelperText>
      )}
    </Box>
  );
}

UploadField.propTypes = {
  name: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  value: PropTypes.array,
  error: PropTypes.string,
  // Formik reports an array-valued field's touched state as an array of booleans,
  // not a boolean.
  touched: PropTypes.oneOfType([PropTypes.bool, PropTypes.array]),
  label: PropTypes.string,
  isRequired: PropTypes.bool,
  helperText: PropTypes.string,
  handleChange: PropTypes.func.isRequired,
  maxFiles: PropTypes.number,
  readOnly: PropTypes.bool,
  setFieldTouched: PropTypes.func.isRequired,
};

UploadField.getDefaultValue = uploadField.getDefaultValue;
UploadField.serializeValue = uploadField.serializeValue;
UploadField.deserializeValue = uploadField.deserializeValue;
UploadField.getDisplayValue = uploadField.getDisplayValue;
UploadField.validationSchema = uploadField.validationSchema;
UploadField.graphQlSchema = `
  typeUpload {
    id
    maxFiles
  }
`;

const ACCEPTED_FILE_TYPES = {
  'image/png': ['.png'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/gif': ['.gif'],
  'image/svg+xml': ['.svg'],
  'application/pdf': ['.pdf'],
};

function sortFiles(items) {
  return items;
}
